import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeDa from '@angular/common/locales/da';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteDefaultOptions } from '@angular/material/autocomplete';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS, MatExpansionPanelDefaultOptions } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AppUpdaterService } from '@core/app-updater/app-updater.service';
import { appErrorCodes } from '@core/config/error-codes.const';
import { RoleEnum } from '@core/config/role.enum';
import { CookiePolicyService } from '@core/cookie/cookie-policy.service';
import { DATE_FORMATS } from '@core/date/util/date-format';
import { GlobalErrorHandlerService } from '@core/global-error-handler/global-error-handler.service';
import { WINDOW_REF } from '@core/tokens/window-ref-injection-token.const';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { type FirebaseApp, initializeApp } from 'firebase/app';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { AuthService, DEVELOPER_ROLE, OAUTH_STORAGE, PUBLIC_PAGES } from 'ok-angular-shared/auth';
import { APP_ERROR_CODES, APP_LOG_SERVICE, ErrorMessageService, SnackbarService } from 'ok-angular-shared/components';
import { provideOKDateAdapter } from 'ok-angular-shared/date';
import { EnvironmentLoaderService } from 'ok-angular-shared/services';
import { h401Interceptor } from 'src/interceptors/h401.interceptor';
import { ngswBypassInterceptor } from 'src/interceptors/ngsw-bypass.interceptor';
import { snakeCaseInterceptor } from 'src/interceptors/snake_case.interceptor';
import { timeoutInterceptor } from 'src/interceptors/timeout.interceptor';
import { DEFAULT_TIMEOUT } from 'src/interceptors/tokens/timeout.interceptor.token';
import { versionHeaderInterceptor } from 'src/interceptors/version-header.interceptor';
import { AppSettings } from 'src/settings/appsettings.interface';
import { routes } from './app.routes';
import { CustomIconService } from './services/custom-icon.service';
import { DatadogService } from './services/data-dog.service';
import { getDanishMatDatepickerIntl } from './services/datepicker-intl';
import { getAuthConfig } from './services/get-auth-config.fn';
import { getPaginatorIntl } from './services/paginator-intl';
import { FIREBASE_APP, FIRESTORE } from './shared/services/firebase.tokens';
import { clearTokensOnAuthConfigChange } from './utilities/clear-tokens-on-auth-config-change.fn';
import { showConfigError } from './utilities/show-config-error.fn';
import { stripAuth0PasswordResetParams } from './utilities/strip-auth0-password-reset-params.fn';
import { validateAppSettings } from './utilities/validate-app-settings.fn';

function initApp(
  authService: AuthService,
  dataDogService: DatadogService,
  appUpdaterService: AppUpdaterService,
  customIconService: CustomIconService,
  cookiePolicyService: CookiePolicyService,
  environmentLoaderService: EnvironmentLoaderService<AppSettings>,
  storageService: OAuthStorage
) {
  return async () => {
    stripAuth0PasswordResetParams();
    registerLocaleData(localeDa);

    let config: AppSettings;
    try {
      config = await environmentLoaderService.load({ loadBuildData: true });
      validateAppSettings(config);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ukendt fejl ved indlæsning af konfiguration.';
      showConfigError(message);
      throw error;
    }

    const authConfig = getAuthConfig(config);
    clearTokensOnAuthConfigChange(authConfig, storageService);
    await authService.runInitialLoginSequence(authConfig, true);

    await dataDogService.init();
    cookiePolicyService.init();
    customIconService.init();
    appUpdaterService.init();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    AuthService,
    DatadogService,
    SnackbarService,
    CustomIconService,
    ErrorMessageService,
    CookiePolicyService,
    EnvironmentLoaderService,
    provideAnimations(),
    provideRouter(routes),
    ...provideOKDateAdapter(DATE_FORMATS),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([versionHeaderInterceptor, h401Interceptor, timeoutInterceptor, snakeCaseInterceptor, ngswBypassInterceptor])
    ),
    provideZoneChangeDetection(),
    provideOAuthClient({
      resourceServer: {
        sendAccessToken: true,
        customUrlValidation: (url: string) => {
          const env = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService).config;
          return url.includes(env.subscriptionServiceBaseUrl);
        },
      },
    }),
    {
      provide: FIREBASE_APP,
      useFactory: (envService: EnvironmentLoaderService<AppSettings>) => initializeApp(envService.config.firebaseConfig),
      deps: [EnvironmentLoaderService],
    },
    {
      provide: FIRESTORE,
      useFactory: (firebaseApp: FirebaseApp, envService: EnvironmentLoaderService<AppSettings>) => {
        const firestore = getFirestore(firebaseApp);
        const firebaseConfig = envService.config.firebaseConfig;
        if (firebaseConfig.useEmulator) {
          connectFirestoreEmulator(firestore, firebaseConfig.firestoreEmulatorHost, firebaseConfig.firestoreEmulatorPort);
        }
        return firestore;
      },
      deps: [FIREBASE_APP, EnvironmentLoaderService],
    },
    provideAppInitializer(() => {
      const initializerFn = initApp(
        inject(AuthService),
        inject(DatadogService),
        inject(AppUpdaterService),
        inject(CustomIconService),
        inject(CookiePolicyService),
        inject(EnvironmentLoaderService),
        inject(OAUTH_STORAGE)
      );
      return initializerFn();
    }),
    { provide: LOCALE_ID, useValue: 'da-DK' },
    { provide: WINDOW_REF, useValue: window },
    { provide: DEFAULT_TIMEOUT, useValue: 30000 },
    { provide: APP_ERROR_CODES, useValue: appErrorCodes },
    { provide: APP_LOG_SERVICE, useClass: DatadogService },
    { provide: MatPaginatorIntl, useFactory: getPaginatorIntl },
    { provide: MatDatepickerIntl, useFactory: getDanishMatDatepickerIntl },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: '' } },
    { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true, scrollStrategy: new NoopScrollStrategy() },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', color: 'accent', subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
    },
    {
      provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
      useValue: { collapsedHeight: 'auto', expandedHeight: 'auto' } as MatExpansionPanelDefaultOptions,
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { autoActiveFirstOption: true } as MatAutocompleteDefaultOptions,
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideIcon: 'true' },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        touchGestures: 'auto',
        touchLongPressShowDelay: 0,
        touchendHideDelay: 3500,
      } as MatTooltipDefaultOptions,
    },
    {
      provide: DEVELOPER_ROLE,
      useValue: RoleEnum.Developer,
    },
    { provide: OAUTH_STORAGE, useValue: localStorage },
    { provide: PUBLIC_PAGES, useValue: ['/installation-info/', '/unsubscribe/'] },
  ],
};
